// Pure financial math used by calculators. All rates are annual percentages.

export function sipSeries(monthly, ratePct, years, stepUpPct = 0) {
  const i = ratePct / 100 / 12;
  let value = 0, invested = 0, sip = monthly;
  const rows = [];
  for (let y = 1; y <= years; y++) {
    for (let m = 0; m < 12; m++) {
      value = (value + sip) * (1 + i);
      invested += sip;
    }
    rows.push({ year: `Y${y}`, invested: Math.round(invested), value: Math.round(value) });
    sip = sip * (1 + stepUpPct / 100);
  }
  return { invested, value, gains: value - invested, rows };
}

export function lumpsumSeries(principal, ratePct, years, expensePct = 0) {
  const r = (ratePct - expensePct) / 100;
  const rows = [];
  for (let y = 1; y <= years; y++) {
    rows.push({ year: `Y${y}`, invested: principal, value: Math.round(principal * Math.pow(1 + r, y)) });
  }
  const value = principal * Math.pow(1 + r, years);
  return { invested: principal, value, gains: value - principal, netRate: r * 100, rows };
}

export function compoundSeries(principal, ratePct, years, perYear) {
  const r = ratePct / 100;
  const rows = [];
  for (let y = 1; y <= years; y++) {
    const simple = principal * (1 + r * y);
    rows.push({ year: `Y${y}`, invested: Math.round(simple), value: Math.round(principal * Math.pow(1 + r / perYear, perYear * y)) });
  }
  const value = principal * Math.pow(1 + r / perYear, perYear * years);
  const effective = (Math.pow(1 + r / perYear, perYear) - 1) * 100;
  return { invested: principal, value, interest: value - principal, effective, rows };
}

// Monthly SIP (start-of-month) required to reach `target` in `years`.
export function sipNeeded(target, ratePct, years) {
  const n = years * 12;
  if (target <= 0 || n <= 0) return 0;
  const i = ratePct / 100 / 12;
  if (i === 0) return target / n;
  return target / (((Math.pow(1 + i, n) - 1) / i) * (1 + i));
}

export function retirementPlan({ age, retireAge, lifeExpectancy, monthlyExpense, inflation, preReturn, postReturn, savings }) {
  const yearsToRetire = Math.max(retireAge - age, 0);
  const retirementYears = Math.max(lifeExpectancy - retireAge, 0);
  const monthlyAtRetirement = monthlyExpense * Math.pow(1 + inflation / 100, yearsToRetire);
  const annual = monthlyAtRetirement * 12;
  const q = (1 + inflation / 100) / (1 + postReturn / 100);
  const corpus = Math.abs(q - 1) < 1e-9 ? annual * retirementYears : annual * (1 - Math.pow(q, retirementYears)) / (1 - q);
  const savingsFV = savings * Math.pow(1 + preReturn / 100, yearsToRetire);
  const shortfall = Math.max(corpus - savingsFV, 0);
  const monthlySip = yearsToRetire > 0 ? sipNeeded(shortfall, preReturn, yearsToRetire) : shortfall;
  return { yearsToRetire, retirementYears, monthlyAtRetirement, corpus, savingsFV, shortfall, monthlySip };
}

export function insuranceNeed({ annualIncome, years, loans, goals, existingCover, savings }) {
  const incomeReplacement = annualIncome * years;
  const gross = incomeReplacement + loans + goals;
  const need = Math.max(gross - existingCover - savings, 0);
  return { incomeReplacement, gross, need };
}

export function goalPlan({ cost, years, inflation, returnPct, saved }) {
  const futureCost = cost * Math.pow(1 + inflation / 100, years);
  const savedFV = saved * Math.pow(1 + returnPct / 100, years);
  const gap = Math.max(futureCost - savedFV, 0);
  const monthlySip = sipNeeded(gap, returnPct, years);
  const lumpsumToday = gap / Math.pow(1 + returnPct / 100, years);
  return { futureCost, savedFV, gap, monthlySip, lumpsumToday };
}
